import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Search, Sparkles } from "lucide-react";

interface AISuggestSeriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSeriesSelected?: (seriesData: any) => void;
}

export function AISuggestSeriesDialog({
  open,
  onOpenChange,
  onSeriesSelected,
}: AISuggestSeriesDialogProps) {
  const [seriesName, setSeriesName] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);

  const handleSearch = async () => {
    if (!seriesName.trim()) {
      toast.error("Please enter a series name");
      return;
    }

    setSearching(true);
    try {
      // Call ISBNdb to find series information
      const { data, error } = await supabase.functions.invoke('suggest-series-books', {
        body: {
          seriesName,
          authorName: authorName || undefined,
        },
      });

      if (error) throw error;

      if (data?.suggestions && data.suggestions.length > 0) {
        setSuggestions(data.suggestions);
      } else {
        toast.info("No series found. Try adjusting the name or author.");
        setSuggestions([]);
      }
    } catch (error: any) {
      console.error('AI search error:', error);
      toast.error(`Search failed: ${error.message}`);
      setSuggestions([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSeries = (series: any) => {
    onSeriesSelected?.(series);
    onOpenChange(false);
    toast.success("Series suggestion created! You can now edit and approve it.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Series Suggestion
          </DialogTitle>
          <DialogDescription>
            Enter a series name and let AI find all the books in the series
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="seriesName">Series Name</Label>
              <Input
                id="seriesName"
                placeholder="e.g., Harry Potter"
                value={seriesName}
                onChange={(e) => setSeriesName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="authorName">Author (Optional)</Label>
              <Input
                id="authorName"
                placeholder="e.g., J.K. Rowling"
                value={authorName}
                onChange={(e) => setAuthorName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
          </div>

          <Button
            onClick={handleSearch}
            disabled={searching || !seriesName.trim()}
            className="w-full"
          >
            {searching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                AI is searching...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Find Series with AI
              </>
            )}
          </Button>

          {suggestions.length > 0 && (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">AI Found These Series:</h3>
                {suggestions.map((series: any, idx: number) => (
                  <Card key={idx} className="p-4 hover:border-primary transition-colors">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-lg">{series.seriesName}</h4>
                          <p className="text-sm text-muted-foreground">by {series.authorName}</p>
                        </div>
                        <Badge variant="secondary">
                          {series.books?.length || 0} books found
                        </Badge>
                      </div>

                      {series.books && series.books.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {series.books.slice(0, 5).map((book: any, bookIdx: number) => (
                            <div key={bookIdx} className="flex flex-col items-center min-w-[80px]">
                              <div className="relative">
                                <div className="w-[80px] h-[120px] bg-muted rounded flex items-center justify-center text-xs text-center p-2">
                                  {book.title}
                                </div>
                                <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
                                  {book.position || bookIdx + 1}
                                </div>
                              </div>
                            </div>
                          ))}
                          {series.books.length > 5 && (
                            <div className="flex items-center text-muted-foreground text-sm">
                              +{series.books.length - 5} more
                            </div>
                          )}
                        </div>
                      )}

                      <Button
                        className="w-full"
                        onClick={() => handleSelectSeries(series)}
                      >
                        Select & Edit This Series
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}